import React from 'react';

const Card = ({ 
  children, 
  variant = 'basic',
  className = '', 
  padding = 'default',
  hover = true,
  onClick,
  interactive = false,
  ...props 
}) => {
  const baseClasses = "bg-white rounded-2xl ring-1 ring-border transition-all duration-300 ease-smooth";
  
  const variantClasses = {
    basic: "shadow-lg",
    elevated: "shadow-xl hover:shadow-2xl",
    interactive: "shadow-lg hover:shadow-xl cursor-pointer",
    feature: "shadow-lg hover:shadow-glow group",
    minimal: "shadow-sm",
    outlined: "shadow-none border-2 border-border",
  };

  const paddingClasses = {
    none: "",
    small: "p-4",
    default: "p-6 lg:p-8",
    large: "p-8 lg:p-12",
  };

  const hoverClasses = hover ? "hover:ring-primary/20" : "";
  const interactiveClasses = interactive || onClick ? "cursor-pointer hover:shadow-xl" : "";

  const classes = [
    baseClasses,
    variantClasses[variant],
    paddingClasses[padding],
    hoverClasses,
    interactiveClasses,
    className
  ].filter(Boolean).join(' ');

  const Component = onClick || interactive ? 'button' : 'div';

  return (
    <Component
      className={classes}
      onClick={onClick}
      {...props}
    >
      {children}
    </Component>
  );
};

// Card sub-components for consistent structure
Card.Header = ({ children, className = '', ...props }) => (
  <div className={`mb-4 ${className}`} {...props}>
    {children}
  </div>
);

Card.Title = ({ children, className = '', size = 'default', ...props }) => {
  const sizeClasses = {
    small: "text-lg font-semibold",
    default: "text-xl font-semibold",
    large: "text-2xl font-bold",
  };

  return (
    <h3 className={`text-textPrimary mb-2 ${sizeClasses[size]} ${className}`} {...props}>
      {children}
    </h3>
  );
};

Card.Subtitle = ({ children, className = '', ...props }) => (
  <p className={`text-textSecondary text-sm ${className}`} {...props}>
    {children}
  </p>
);

Card.Content = ({ children, className = '', ...props }) => (
  <div className={`text-textSecondary leading-relaxed ${className}`} {...props}>
    {children}
  </div>
);

Card.Footer = ({ children, className = '', ...props }) => (
  <div className={`mt-6 pt-4 border-t border-border ${className}`} {...props}>
    {children}
  </div>
);

Card.Icon = ({ children, className = '', size = 'default', ...props }) => {
  const sizeClasses = {
    small: "w-12 h-12",
    default: "w-16 h-16 lg:w-20 lg:h-20",
    large: "w-24 h-24",
  };

  return (
    <div className={`inline-flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl mb-4 ${sizeClasses[size]} ${className}`} {...props}>
      {children}
    </div>
  );
};

export default Card;
